// Inline product screenshot for the "Getting Started with Companion" post.
//
// When `src` is provided (an HTML chat bundled into public/blog/...) we render
// it in an iframe — that's the design's intended treatment.
// When `src` is omitted, we fall back to a striped placeholder card so we can
// ship sections before their screenshots are ready.
import type { CSSProperties } from "react";

import { MediaCaption } from "../../components/MediaCaption";

type ScreenshotCompanion = "Quill" | "Summi" | "Sage" | "Otto";

const ACCENTS: Record<ScreenshotCompanion, { bar: string; tint: string }> = {
  Quill: { bar: "#F59E0B", tint: "#FEF3C7" },
  Summi: { bar: "#EC4899", tint: "#FCE7F3" },
  Sage: { bar: "#10B981", tint: "#D1FAE5" },
  Otto: { bar: "#6366F1", tint: "#E0E7FF" },
};

type ScreenshotPlaceholderProps = {
  companion?: ScreenshotCompanion;
  caption?: string;
  aspect?: string;
  src?: string;
};

function CompanionBadge({
  companion,
  bar,
}: {
  companion: ScreenshotCompanion;
  bar: string;
}) {
  return (
    <div
      className="font-companion absolute top-3.5 left-3.5 z-[1] inline-flex items-center gap-2 rounded-full border-[1.5px] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em]"
      style={{ borderColor: bar, color: bar }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: bar }} />
      {companion}
    </div>
  );
}

export function ScreenshotPlaceholder({
  companion = "Sage",
  caption,
  aspect = "16/9",
  src,
}: ScreenshotPlaceholderProps) {
  const a = ACCENTS[companion];

  if (src) {
    return (
      <figure className="my-8">
        <div className="overflow-hidden rounded-[18px] border border-grindigo-100 bg-white shadow-[0_18px_40px_-24px_rgba(67,56,202,0.35)]">
          <div
            className="relative w-full bg-[#FAF9F5]"
            style={{ aspectRatio: aspect } as CSSProperties}
          >
            <CompanionBadge companion={companion} bar={a.bar} />
            <iframe
              src={src}
              title={`${companion} — example chat`}
              loading="lazy"
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>
        {caption && <MediaCaption as="figcaption">{caption}</MediaCaption>}
      </figure>
    );
  }

  const stripeStyle: CSSProperties = {
    aspectRatio: aspect,
    backgroundImage: `repeating-linear-gradient(135deg, ${a.tint} 0 14px, white 14px 28px)`,
  };

  return (
    <figure className="my-8">
      <div className="overflow-hidden rounded-[18px] border border-grindigo-100 bg-white shadow-[0_18px_40px_-24px_rgba(67,56,202,0.35)]">
        <div className="relative grid place-items-center" style={stripeStyle}>
          <CompanionBadge companion={companion} bar={a.bar} />
          <div className="m-8 max-w-[520px] rounded-[14px] border border-grindigo-100 bg-white px-4 py-3.5 text-left text-[13.5px] leading-[1.55] text-grindigo-700 shadow-[0_8px_20px_-12px_rgba(67,56,202,0.25)]">
            <div
              className="font-companion mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em]"
              style={{ color: a.bar }}
            >
              Screenshot placeholder
            </div>
            {caption}
          </div>
        </div>
      </div>
      {caption && (
        <figcaption className="mt-3 text-center font-body text-[13.5px] italic leading-[1.5] text-secondary">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
